import { useState, useEffect, useCallback } from 'react';
import { useDataCache } from '../contexts/BreakdownDataCacheContext';
import { SlotType } from '../types/schedule.types';
import { getRestrictedDatesAsDateStrings } from '../types/element.types';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 计算哪些 element 的受限日期（elementRestrictedDates）与拍摄计划日期（slotDate）有重叠。
 * 重叠即冲突日期：同时出现在 elementRestrictedDates 和 slotDate 中的日期。
 * 用于元素管理页：当存在重叠时，将该 element 的文本颜色设为冲突日期色（红色）。
 */
export function useElementRestrictedDatesOverlap(
  breakdownID: string | undefined,
  scheduleID: string | undefined
) {
  const {
    getElementCache,
    subscribeElementCache,
    getElementRelationCache,
    subscribeElementRelationCache,
    getSlotCache,
    subscribeSlotCache,
  } = useDataCache();

  const [overlapIds, setOverlapIds] = useState<Set<string>>(new Set());

  const updateOverlap = useCallback(() => {
    if (!breakdownID || !scheduleID) {
      setOverlapIds(new Set());
      return;
    }
    const itemCache = getElementCache(breakdownID);
    const relationCache = getElementRelationCache(breakdownID);
    const slotCache = getSlotCache(scheduleID);
    const elements = itemCache?.elements ?? [];
    const relations = relationCache?.elementRelations ?? [];
    const slots = slotCache?.slots ?? [];

    // elementID -> Set<sceneID>
    const elementToSceneIDs = new Map<string, Set<string>>();
    for (const rel of relations) {
      if (!rel.elementID || !rel.sceneID) continue;
      let set = elementToSceneIDs.get(rel.elementID);
      if (!set) {
        set = new Set<string>();
        elementToSceneIDs.set(rel.elementID, set);
      }
      set.add(rel.sceneID);
    }

    // sceneID -> Set<slotDate>
    const sceneToCalDates = new Map<string, Set<string>>();
    for (const pt of slots) {
      if (
        (pt.slotType !== SlotType.SHOOT && pt.slotType) ||
        !pt.slotBelongSceneID ||
        !pt.slotDate
      )
        continue;
      const dateStr =
        typeof pt.slotDate === 'string'
          ? pt.slotDate.slice(0, 10)
          : String(pt.slotDate);
      if (!DATE_REGEX.test(dateStr)) continue;
      const sceneID = pt.slotBelongSceneID;
      let set = sceneToCalDates.get(sceneID);
      if (!set) {
        set = new Set<string>();
        sceneToCalDates.set(sceneID, set);
      }
      set.add(dateStr);
    }

    const result = new Set<string>();
    for (const item of elements) {
      const id = item.elementID;
      if (!id) continue;
      const restrictedDates = getRestrictedDatesAsDateStrings(item.elementRestrictedDates);
      if (restrictedDates.length === 0) continue;
      const sceneIDs = elementToSceneIDs.get(id);
      if (!sceneIDs || sceneIDs.size === 0) continue;
      const calDates = new Set<string>();
      Array.from(sceneIDs).forEach((sid) => {
        const dates = sceneToCalDates.get(sid);
        if (dates) dates.forEach((d) => calDates.add(d));
      });
      const hasOverlap = restrictedDates.some((dateStr) => calDates.has(dateStr));
      if (hasOverlap) result.add(id);
    }
    setOverlapIds(result);
  }, [
    breakdownID,
    scheduleID,
    getElementCache,
    getElementRelationCache,
    getSlotCache,
  ]);

  useEffect(() => {
    updateOverlap();
    if (!breakdownID || !scheduleID) return undefined;
    const unsub1 = subscribeElementCache(breakdownID, updateOverlap);
    const unsub2 = subscribeElementRelationCache(breakdownID, updateOverlap);
    const unsub3 = subscribeSlotCache(scheduleID, updateOverlap);
    return () => {
      unsub1();
      unsub2();
      unsub3();
    };
  }, [breakdownID, scheduleID, updateOverlap, subscribeElementCache, subscribeElementRelationCache, subscribeSlotCache]);

  return overlapIds;
}
